import { useCallback, useState } from 'react';
import { type ConsultingOffer, type PreparationStage } from '../../types';
import { trackEvent, markFunnelStage } from '@/shared/analytics';
import { recordStage } from '../../api';
import { invitesConsulting, stageIntent } from '../stages';

export type AnswerStep =
  | { name: 'pick' }
  | { name: 'consulting'; offer: ConsultingOffer; alreadyRequested: boolean }
  | { name: 'nextStep'; stage: PreparationStage }
  | { name: 'connect' };

interface UseStageAnswerOptions {
  stage: PreparationStage | null;
  consulting?: ConsultingOffer | null;
  consultingRequested?: boolean;
}

export interface UseStageAnswerReturn {
  stage: PreparationStage | null;
  step: AnswerStep;
  chooseStage: (stage: PreparationStage) => void;
  promoteStage: (stage: PreparationStage) => void;
  saveFailed: boolean;
  retrySave: () => void;
  reset: () => void;
}

function toAnswerStep(
  stage: PreparationStage,
  consulting: ConsultingOffer | null,
  consultingRequested: boolean
): AnswerStep {
  if (stageIntent(stage) === 'schedule') return { name: 'connect' };
  if (invitesConsulting(stage) && consulting) {
    return { name: 'consulting', offer: consulting, alreadyRequested: consultingRequested };
  }

  return { name: 'nextStep', stage };
}

export function useStageAnswer({
  stage: recordedStage,
  consulting = null,
  consultingRequested = false,
}: UseStageAnswerOptions): UseStageAnswerReturn {
  const [stage, setStage] = useState<PreparationStage | null>(recordedStage);
  const [answered, setAnswered] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const save = useCallback((value: PreparationStage) => {
    setSaveFailed(false);

    recordStage(value).catch(() => {
      setSaveFailed(true);
      trackEvent('preparation_stage_save_failed', { stage: value });
    });
  }, []);

  const chooseStage = useCallback(
    (chosen: PreparationStage) => {
      setStage(chosen);
      setAnswered(true);
      markFunnelStage(chosen, 'preparation_stage_selected', { stage: chosen, intent: stageIntent(chosen) });
      save(chosen);
    },
    [save]
  );

  const promoteStage = useCallback((next: PreparationStage) => {
    setStage(next);
  }, []);

  const retrySave = useCallback(() => {
    if (stage) save(stage);
  }, [stage, save]);

  const reset = useCallback(() => {
    setAnswered(false);
  }, []);

  const step: AnswerStep =
    !answered || stage === null ? { name: 'pick' } : toAnswerStep(stage, consulting, consultingRequested);

  return { stage, step, chooseStage, promoteStage, saveFailed, retrySave, reset };
}
